import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';

import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-bridge/constants';
import { createBuiltInColorMap, readableTextColor } from '@atlassian/color-picker';
import { generateThemeBranding } from '@atlassian/csm-colour-theme-generator/generate-theme-branding';

import {
	HUB_PRIMARY_COLOR_CSS_VAR,
	HUB_PRIMARY_LINK_COLOR_CSS_VAR,
	HUB_PRIMARY_TEXT_COLOR_CSS_VAR,
} from '../../constants';
import { useHubContext } from '../../utils';
import { HUB_COLOR_CSS_VARS, toAdfColor, useHubBrandPalette } from '../../utils/hub-brand-palette';
import type { PaletteRef } from '../../utils/hub-brand-palette';
import {
	HUB_BORDER_NEUTRAL_BOLD_HEX,
	HUB_BORDER_NEUTRAL_HEX,
	HUB_LOCKED_BORDER_NEUTRAL_HEXES,
	HUB_LOCKED_NEUTRAL_HEXES,
} from '../../utils/hub-brand-palette/locked-color-map';
import { HUB_CORNER_RADIUS_CSS_VAR, resolveHubCornerRadius } from '../../utils/hub-corner-radius';
import type { SpaceDensity } from '../../utils/hub-space-density/types';
import { buildHubThemingStateAttributes } from '../../utils/hub-theming-analytics';
import { DEFAULT_ACCENT, DEFAULT_PRIMARY } from '../../utils/hub-theming-defaults';
import type { HubTypography } from '../../utils/hub-typography';
import { getHubDefaultParameters } from '../get-hub-default-parameters';
import { useIsAdvancedThemingEnabled } from '../use-is-advanced-theming-enabled';
import { useShouldShowPremiumUpsell } from '../use-should-show-premium-upsell';

import type { HubThemingContextValue } from './types';

// Re-exported for backwards compatibility: these moved to `locked-color-map`
// so `usePickerColorMap` can read them without importing this module, which
// the config-panel field tests mock.
export { HUB_BORDER_NEUTRAL_BOLD_HEX, HUB_BORDER_NEUTRAL_HEX };

const HubThemingContext: React.Context<HubThemingContextValue | null> =
	createContext<HubThemingContextValue | null>(null);

const readableHubTextColor = (color: string): string =>
	readableTextColor(color, '@atlassian/company-hub-common');

export const HubThemingProvider = ({ children }: { children: ReactNode }): JSX.Element => {
	di(useHubContext, useIsAdvancedThemingEnabled, useAnalyticsEvents, useShouldShowPremiumUpsell);
	const { branding, env, hubType, isInPreviewMode } = useHubContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isEnabled = useIsAdvancedThemingEnabled();
	const isBrandPaletteLocked = useShouldShowPremiumUpsell();
	const primaryColor = branding?.colors?.primary ?? DEFAULT_PRIMARY;

	// Accent comes from the BE (`HelpCenterBrandingColors.secondaryColor`,
	// mapped to `branding.colors.accent` at the bootstrap boundary). If the
	// BE returns nothing — pre-v2 hubs, non-help-center hosts, fresh tenants
	// — we fall through to `DEFAULT_ACCENT`. The panel persists changes via
	// the same Relay mutation path that primary uses, so the picker's
	// `value` prop and the provider's `accentColor` here stay in lockstep
	// via the next bootstrap re-render. No FE-side state needed.
	const accentColor = branding?.colors?.accent ?? DEFAULT_ACCENT;

	const palette = useHubBrandPalette({ primary: primaryColor, accent: accentColor });

	const primaryTextColor = useMemo(() => readableHubTextColor(primaryColor), [primaryColor]);
	const primaryLinkColor = useMemo(
		() => generateThemeBranding(primaryColor).color900,
		[primaryColor],
	);

	// Typography source of truth is the BE-persisted value, surfaced on
	// `branding.typography` at the bootstrap boundary. This is what makes the
	// help-seeker VIEW pages (homepage, profile, login, request list/details)
	// render the admin's chosen font — they read the same branding the editor
	// does. `setHubTypography` layers an in-session override on top for instant
	// edit-mode preview; the override is persisted on Publish via the branding
	// mutation (see action-bar), after which a bootstrap re-read makes the base
	// value match. View mode never calls the setter, so it always shows the
	// persisted value. Undefined (no BE value, no override) → HubTypographyScope
	// no-ops → Atlassian Sans default.
	const brandingTypography = branding?.typography;
	const [typographyOverride, setTypographyOverride] = useState<HubTypography | undefined>(
		undefined,
	);
	const typography = typographyOverride ?? brandingTypography;
	const setHubTypography = useCallback((next: HubTypography) => {
		setTypographyOverride(next);
	}, []);

	const brandingSpaceDensity = branding?.spaceDensity;
	const [spaceDensityOverride, setSpaceDensityOverride] = useState<SpaceDensity | undefined>(
		undefined,
	);
	const spaceDensity = spaceDensityOverride ?? brandingSpaceDensity;
	const setSpaceDensity = useCallback((next: SpaceDensity) => {
		setSpaceDensityOverride(next);
	}, []);

	// Corner radius source of truth is the BE-persisted value, surfaced on
	// `branding.cornerRadius` at the bootstrap boundary. `setHubCornerRadius`
	// layers an in-session override on top for instant edit-mode preview; the
	// override is persisted on Publish via the branding mutation. View mode
	// never calls the setter. Undefined / invalid → resolves to 8px default.
	const brandingCornerRadius = branding?.cornerRadius;
	const [cornerRadiusOverride, setCornerRadiusOverride] = useState<number | undefined>(undefined);
	const cornerRadius = resolveHubCornerRadius(cornerRadiusOverride ?? brandingCornerRadius);
	const setHubCornerRadius = useCallback((next: number) => {
		setCornerRadiusOverride(next);
	}, []);

	// Adoption denominator: fire once per hub load (view or edit) with the
	// persisted theming state, but only when advanced theming is enabled. The
	// provider mounts on every hub load via HubBootstrap, so this captures the
	// full installed base; deduped downstream by helpCenterAri.
	const hasFiredRenderedRef = useRef(false);
	useEffect(() => {
		if (hasFiredRenderedRef.current || !isEnabled) {
			return;
		}
		if (!env?.helpCenterAri) {
			return;
		}
		hasFiredRenderedRef.current = true;
		// Flat analytics-bridge envelope (top-level `analyticsType`): this is the shape
		// help-center's analytics listener chain extracts and forwards to GAS v3. A nested
		// `{ type, data }` envelope resolves `analyticsType: undefined` and is silently
		// dropped on this host, so we mirror hub-home's `help_hub published` pattern.
		createAnalyticsEvent({
			action: 'rendered',
			actionSubject: 'helpHubTheme',
			analyticsType: TRACK_EVENT_TYPE,
			attributes: {
				source: 'hubThemingProvider',
				helpCenterAri: env.helpCenterAri,
				helpCenterId: env.helpCenterId,
				hubType,
				isInPreviewMode: !!isInPreviewMode,
				...buildHubThemingStateAttributes({
					primaryColor,
					accentColor,
					typography,
					cornerRadius,
					spaceDensity,
				}),
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		env,
		hubType,
		isInPreviewMode,
		isEnabled,
		primaryColor,
		accentColor,
		typography,
		cornerRadius,
		spaceDensity,
	]);

	// `defaultParameters` and `brandBackgroundColorMap` deliberately split on
	// `isEnabled` so consumers (CommonHubExtensionsDependenciesProvider, the
	// extension config panels) can keep reading from `useHubTheming()` without
	// branching themselves. When the gate is OFF we restore the pre-v2 shape
	// exactly: legacy CSS-var refs in default params (consumed by
	// setHubBrandingStyle's writes) and a single-knownColor swatch grid built
	// from `branding.colors.primary` so the picker still shows the brand swatch.
	//
	// When ON, the wrapper div below emits `--hub-color-*` vars, and
	// `defaultParameters.backgroundColor` is a `var(--hub-color-primary-200)`
	// reference rather than a hex literal. The renderer drops the var ref into
	// inline style; the cascade resolves it against the wrapper. This means
	// brand-color edits trigger a CSS-only repaint — no React re-render in the
	// consumer is needed for the new color to apply.
	const defaultParameters = useMemo(
		() =>
			isEnabled
				? // Gate ON: resolver returns `var(--hub-color-primary-200)` + readable
					// text; when locked it seeds the raw `primaryColor` instead.
					{
						...getHubDefaultParameters({ palette, isBrandPaletteLocked, primaryColor }),
						textColor: primaryTextColor,
					}
				: // Gate OFF: same resolver with no palette → legacy `var(--hub-*)`
					// chain that resolves against setHubBrandingStyle's writes on <html>.
					getHubDefaultParameters(),
		[isEnabled, palette, primaryTextColor, isBrandPaletteLocked, primaryColor],
	);

	// Brand swatch grid, shared by every bespoke element picker via context:
	//   - ON + unlocked: full palette + white/black.
	//   - ON + locked: the admin's primary brand colour + white/black (gating the
	//     source here reduces every picker at once; each adds its own current
	//     value + custom-hex). Also see `brandBorderColorMap` below.
	//   - OFF: legacy single CSS-var string (matches master's strict-equality
	//     swatch highlight for pre-v2 ADFs).
	//
	// White and black are offered to locked tenants too: they are neutrals, not
	// palette tints, so they cost nothing in entitlement terms — and without
	// them a text or heading picker with a dark brand colour has no legible
	// choice at all beyond typing a custom hex.
	const brandBackgroundColorMap = useMemo(() => {
		if (!isEnabled) {
			return createBuiltInColorMap({ knownColors: [defaultParameters.backgroundColor] });
		}
		if (isBrandPaletteLocked) {
			return createBuiltInColorMap({
				knownColors: [primaryColor, ...HUB_LOCKED_NEUTRAL_HEXES],
			});
		}
		return createBuiltInColorMap({
			knownColors: [...Object.values(palette), ...HUB_LOCKED_NEUTRAL_HEXES],
		});
	}, [isEnabled, isBrandPaletteLocked, primaryColor, palette, defaultParameters]);

	// Same as brandBackgroundColorMap but with 4 neutrals appended (white,
	// color.border, color.border.bold, black); consumer translates the neutral
	// hexes back to token refs at onChange. Locked tenants get the primary plus
	// the same 4 neutrals — a border is neutral far more often than it is brand.
	const brandBorderColorMap = useMemo(() => {
		if (!isEnabled) {
			return createBuiltInColorMap({ knownColors: [defaultParameters.backgroundColor] });
		}
		if (isBrandPaletteLocked) {
			return createBuiltInColorMap({
				knownColors: [primaryColor, ...HUB_LOCKED_BORDER_NEUTRAL_HEXES],
			});
		}
		return createBuiltInColorMap({
			knownColors: [...Object.values(palette), ...HUB_LOCKED_BORDER_NEUTRAL_HEXES],
		});
	}, [isEnabled, isBrandPaletteLocked, primaryColor, palette, defaultParameters]);

	// Bound once here so no element panel has to remember to pass the locked
	// state through to `toAdfColor` — forgetting it is what let locked tenants
	// persist palette-shade refs that stop tracking the brand colour.
	const toStoredColor = useCallback(
		(hex: string) => toAdfColor(hex, palette, { isBrandPaletteLocked, primaryColor }),
		[palette, isBrandPaletteLocked, primaryColor],
	);

	const value = useMemo<HubThemingContextValue>(
		() => ({
			isAdvancedThemingEnabled: isEnabled,
			isBrandPaletteLocked,
			primaryColor,
			accentColor,
			primaryTextColor,
			primaryLinkColor,
			palette,
			defaultParameters,
			brandBackgroundColorMap,
			brandBorderColorMap,
			cornerRadius,
			setHubCornerRadius,
			setHubTypography,
			typography,
			setSpaceDensity,
			spaceDensity,
			toStoredColor,
		}),
		[
			isEnabled,
			isBrandPaletteLocked,
			primaryColor,
			accentColor,
			primaryTextColor,
			primaryLinkColor,
			palette,
			defaultParameters,
			brandBackgroundColorMap,
			brandBorderColorMap,
			cornerRadius,
			setHubCornerRadius,
			setHubTypography,
			typography,
			setSpaceDensity,
			spaceDensity,
			toStoredColor,
		],
	);

	// Wrapper div emits the entire palette as inline `style` so the 20
	// `--hub-color-*` custom properties land in the SSR HTML and resolve on
	// first paint — no useEffect, no `document.documentElement.setProperty`,
	// no flash of unthemed content. Everything inside the wrapper inherits the
	// vars through the normal CSS cascade.
	//
	// `display: contents` tells the browser to render the wrapper's children
	// but skip generating a box for the wrapper itself, so it is invisible to
	// flex/grid/positioning. Without it, the wrapper would introduce a
	// `display: block` box that could break ancestor layouts (extra flex item,
	// extra grid cell, broken percentage heights, etc.). We get CSS-var
	// scoping + a DOM marker for devtools without any layout side-effect.
	// The cascade still works — `display: contents` does not stop custom
	// properties from being inherited by descendants.
	//
	// Wrapper always emits the full palette + legacy vars, regardless of the
	// gate state. This matters when an admin edits under v2 ON and then toggles
	// the FF off — their persisted ADFs reference `--hub-color-primary-*` and
	// would resolve to `unset` (white) without the wrapper providing the vars.
	// Always emitting keeps a flag flip non-destructive while the resolver and
	// picker still gate their *behavior* on `isAdvancedThemingEnabled`.
	//
	// Legacy compat vars (`--hub-primary-color` / `--hub-primary-text-color` /
	// `--hub-primary-link-color`) are also always emitted so pre-v2 ADFs that
	// reference them render with the exact same colors as before the v2 flip
	// (the input hex verbatim, WCAG-readable text, and
	// `generateThemeBranding(...).color900` link tint). Matches
	// `setHubBrandingStyle`'s semantics so admins with already-customised hubs
	// see no visual change post-flip.
	const wrapperStyle = useMemo<React.CSSProperties>(
		() =>
			({
				display: 'contents',
				...buildPaletteStyle(palette),
				[HUB_PRIMARY_COLOR_CSS_VAR]: primaryColor,
				[HUB_PRIMARY_TEXT_COLOR_CSS_VAR]: primaryTextColor,
				[HUB_PRIMARY_LINK_COLOR_CSS_VAR]: primaryLinkColor,
				[HUB_CORNER_RADIUS_CSS_VAR]: `${cornerRadius}px`,
			}) as React.CSSProperties,
		[palette, primaryColor, primaryTextColor, primaryLinkColor, cornerRadius],
	);

	return (
		<HubThemingContext.Provider value={value}>
			{/* The `style` prop is genuinely dynamic here: it carries the 20
				`--hub-color-*` CSS custom properties derived from the admin's
				chosen brand colors. These must land inline in the SSR HTML so
				they resolve on first paint (a CSS stylesheet would require an
				extra round-trip + injection effect). The `css` prop / styled
				API can't express runtime-generated CSS custom properties. */}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
			<div data-hub-theme-root="" style={wrapperStyle}>
				{children}
			</div>
		</HubThemingContext.Provider>
	);
};

/**
 * Build the inline-style object emitted on the wrapper div from the palette.
 * Returns 20 entries of the form `{ '--hub-color-primary-200': '#B3D4FF' }`.
 *
 * Kept outside the provider so the entry-set construction is testable in
 * isolation and not entangled with React rendering.
 */
const buildPaletteStyle = (
	palette: ReturnType<typeof useHubBrandPalette>,
): Record<string, string> => {
	const out: Record<string, string> = {};
	for (const ref of Object.keys(palette) as PaletteRef[]) {
		out[HUB_COLOR_CSS_VARS[ref]] = palette[ref];
	}
	return out;
};

/**
 * Reads the live theming state.
 *
 * Returns `undefined` if no `<HubThemingProvider>` is in the tree. This is the
 * cross-host safe path: bespoke extension renderers (spotlight, etc.) also
 * run inside Confluence Company Hub where the provider isn't mounted; they
 * read this hook and treat `undefined` as "advanced theming disabled, take
 * the legacy CSS-var passthrough path".
 *
 * Help-center consumers are guaranteed to have a provider in tree (mounted by
 * `HubBootstrap`), so they can safely use the non-null assertion or treat the
 * value as always-defined.
 */
export const useHubTheming = (): HubThemingContextValue | undefined => {
	return useContext(HubThemingContext) ?? undefined;
};

export type { HubDefaultParameters, HubThemingContextValue } from './types';
